import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import type { Operation } from '@occc-app/offerHelpers/types';
import {
  LimitConfigurationInput,
  LimitFragment,
  Limit_Time_Period,
  LimitCreateMutationVariables,
  LimitUpdateMutationVariables,
  OfferFragment,
} from '@occc/graphql';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

import type {
  TransactionFrequencyLimitFormValue,
  TransactionFrequencyLimitFormValueEnabled,
  TransactionFrequencyLimitRow,
  TransactionFrequencyTimePeriod,
} from './transaction-frequency-limit.types';

export const getNextAvailablePeriod = (
  currentRows: TransactionFrequencyLimitRow[],
  options: { value: TransactionFrequencyTimePeriod }[],
  fallback: TransactionFrequencyTimePeriod,
): TransactionFrequencyTimePeriod => {
  const used = new Set(currentRows.map((r) => r.timePeriod));
  return options.find((o) => !used.has(o.value))?.value ?? fallback;
};

export function hasValidTransactionFrequencyRowsArray(
  value: unknown,
): value is { rows: TransactionFrequencyLimitRow[] } {
  return (
    value != null &&
    typeof value === 'object' &&
    'rows' in value &&
    Array.isArray((value as { rows: unknown }).rows)
  );
}

export function getFormValueByPath(
  values: Record<string, unknown> | undefined,
  formNamePath: string,
): TransactionFrequencyLimitFormValue | undefined {
  if (values == null) return undefined;
  let current: Record<string, unknown> = values;
  const keys = formNamePath.split('.');
  for (let i = 0; i < keys.length - 1; i++) {
    const next = current[keys[i]];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) return undefined;
    current = next as Record<string, unknown>;
  }
  return current[keys[keys.length - 1]] as TransactionFrequencyLimitFormValue | undefined;
}

export function getNormalizedTransactionFrequencyFormValue(
  values: Record<string, unknown> | undefined,
  formNamePath: string,
): TransactionFrequencyLimitFormValue {
  const current = getFormValueByPath(values, formNamePath);
  const isEnabled = current?.isEnabled ?? false;
  const existingRows: TransactionFrequencyLimitRow[] = hasValidTransactionFrequencyRowsArray(
    current,
  )
    ? current.rows
    : [];
  return { isEnabled, rows: existingRows };
}

function toApiTimePeriod(
  period: Exclude<TransactionFrequencyTimePeriod, 'TOTAL'>,
): Limit_Time_Period {
  switch (period) {
    case 'DAY':
      return Limit_Time_Period.Day;
    case 'WEEK':
      return Limit_Time_Period.Week;
    case 'MONTH':
      return Limit_Time_Period.Month;
    default:
      return Limit_Time_Period.Day;
  }
}

export function getTransactionFrequencyLimitInitialValues(
  offer: OfferFragment,
  options?: { excludeTotal?: boolean },
): TransactionFrequencyLimitFormValue {
  const excludeTotal = options?.excludeTotal ?? false;
  const rows: TransactionFrequencyLimitRow[] = [];

  for (const limit of offer.limits) {
    if (limitHelper.isMembershipTransactionCount(limit)) {
      rows.push({
        id: limit.id,
        limit: limit.limit,
        timePeriod: excludeTotal ? 'DAY' : 'TOTAL',
      });
    } else if (limitHelper.isMembershipTransactionCountInTime(limit)) {
      const timePeriod =
        limit.timePeriod === Limit_Time_Period.Day
          ? 'DAY'
          : limit.timePeriod === Limit_Time_Period.Week
          ? 'WEEK'
          : 'MONTH';
      rows.push({
        id: limit.id,
        limit: limit.limit,
        timePeriod,
      });
    }
  }

  return {
    isEnabled: rows.length > 0,
    rows: rows.length > 0 ? rows : [{ limit: 1, timePeriod: 'DAY' }],
  };
}

function getExistingTransactionLimitIds(limits: LimitFragment[]): Set<string> {
  const ids = new Set<string>();
  for (const limit of limits) {
    if (
      limitHelper.isMembershipTransactionCount(limit) ||
      limitHelper.isMembershipTransactionCountInTime(limit)
    ) {
      ids.add(limit.id);
    }
  }
  return ids;
}

function makeLimitCreateOperation(
  offerId: string,
  configuration: LimitConfigurationInput,
): Operation<'limitCreate', LimitCreateMutationVariables> {
  return {
    name: 'limitCreate',
    variables: {
      input: { offerId, configuration },
    },
  };
}

function makeLimitUpdateOperation(
  limitId: string,
  configuration: LimitConfigurationInput,
): Operation<'limitUpdate', LimitUpdateMutationVariables> {
  return {
    name: 'limitUpdate',
    variables: {
      input: { id: limitId, configuration },
    },
  };
}

function getOperationsForRow(
  row: TransactionFrequencyLimitRow,
  currentLimits: LimitFragment[],
  offerId: string,
): Operation[] {
  const existing = row.id ? currentLimits.find((l) => l.id === row.id) : undefined;

  if (row.timePeriod === 'TOTAL') {
    const config: LimitConfigurationInput = {
      membershipTransactionCount: { limit: row.limit },
    };
    if (!existing) {
      return [makeLimitCreateOperation(offerId, config)];
    }
    if (limitHelper.isMembershipTransactionCount(existing) && existing.limit === row.limit) {
      return [];
    }
    if (limitHelper.isMembershipTransactionCount(existing)) {
      return [makeLimitUpdateOperation(existing.id, config)];
    }
    return [
      makeDeleteOperation('limitDelete', existing.id),
      makeLimitCreateOperation(offerId, config),
    ];
  }

  const timePeriod = toApiTimePeriod(row.timePeriod);
  const config: LimitConfigurationInput = {
    membershipTransactionCountInTime: { limit: row.limit, timePeriod },
  };
  if (!existing) {
    return [makeLimitCreateOperation(offerId, config)];
  }
  if (
    limitHelper.isMembershipTransactionCountInTime(existing) &&
    existing.limit === row.limit &&
    existing.timePeriod === timePeriod
  ) {
    return [];
  }
  if (limitHelper.isMembershipTransactionCountInTime(existing)) {
    return [makeLimitUpdateOperation(existing.id, config)];
  }
  return [
    makeDeleteOperation('limitDelete', existing.id),
    makeLimitCreateOperation(offerId, config),
  ];
}

function isEnabledFormValue(
  v: NonNullable<TransactionFrequencyLimitFormValue>,
): v is TransactionFrequencyLimitFormValueEnabled {
  return v.isEnabled;
}

export function getTransactionFrequencyLimitOperations(
  offerId: string,
  values: TransactionFrequencyLimitFormValue | undefined,
  currentLimits: LimitFragment[],
): Operation[] {
  if (!values) {
    return [];
  }

  if (!isEnabledFormValue(values)) {
    const operations: Operation[] = [];
    for (const limit of currentLimits) {
      if (
        limitHelper.isMembershipTransactionCount(limit) ||
        limitHelper.isMembershipTransactionCountInTime(limit)
      ) {
        operations.push(makeDeleteOperation('limitDelete', limit.id));
      }
    }
    return operations;
  }

  const existingIds = getExistingTransactionLimitIds(currentLimits);
  const rowIds = new Set(
    values.rows
      .filter((r): r is TransactionFrequencyLimitRow & { id: string } => r.id != null)
      .map((r) => r.id),
  );

  const operations: Operation[] = [];
  for (const id of existingIds) {
    if (!rowIds.has(id)) {
      operations.push(makeDeleteOperation('limitDelete', id));
    }
  }

  for (const row of values.rows) {
    operations.push(...getOperationsForRow(row, currentLimits, offerId));
  }

  return operations;
}
